/**
 * Animated Statistics Component
 * Comprehensive counting animation system with progress bars and synchronized icons
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { animated, useSpring } from '@react-spring/web';
import { 
  useScrollAnimation, 
  useAnimationConfig, 
  useReducedMotion, 
  usePerformanceMonitor,
  AnimatedContainer,
  StaggeredList
} from '../lib/animations';
import { Home, Award, Users, Calendar, TrendingUp, Star, CheckCircle } from 'lucide-react';

// Types for statistics configuration
export interface StatisticItem {
  id: string;
  label: string;
  value: number;
  suffix?: string;
  prefix?: string;
  decimals?: number;
  icon?: React.ComponentType<any>;
  color?: string;
  description?: string;
  progressBar?: boolean;
  maxValue?: number; // For progress bar calculation
}

export interface StatisticsConfig {
  title?: string;
  subtitle?: string;
  layout: 'grid' | 'row' | 'column';
  animationStyle: 'sequential' | 'simultaneous' | 'wave';
  countingDuration: number;
  staggerDelay: number;
  showProgressBars: boolean;
  iconAnimation: boolean;
}

interface AnimatedStatisticsProps {
  statistics: StatisticItem[];
  config?: Partial<StatisticsConfig>;
  className?: string;
  triggerOnScroll?: boolean;
  threshold?: number;
}

// Default configuration
const defaultConfig: StatisticsConfig = {
  layout: 'grid',
  animationStyle: 'sequential',
  countingDuration: 2.5,
  staggerDelay: 0.3,
  showProgressBars: false,
  iconAnimation: true
};

// Easing functions for different animation styles
const easingFunctions = {
  easeOutQuart: (t: number) => 1 - Math.pow(1 - t, 4),
  easeOutCubic: (t: number) => 1 - Math.pow(1 - t, 3),
  easeInOutCubic: (t: number) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2,
  bounce: (t: number) => {
    const n1 = 7.5625;
    const d1 = 2.75;
    if (t < 1 / d1) {
      return n1 * t * t;
    } else if (t < 2 / d1) {
      return n1 * (t -= 1.5 / d1) * t + 0.75;
    } else if (t < 2.5 / d1) {
      return n1 * (t -= 2.25 / d1) * t + 0.9375;
    } else {
      return n1 * (t -= 2.625 / d1) * t + 0.984375;
    }
  }
};

// Individual statistic counter component
interface StatisticCounterProps {
  statistic: StatisticItem;
  isVisible: boolean;
  animationDelay: number;
  duration: number;
  showProgressBar: boolean;
  iconAnimation: boolean;
}

const StatisticCounter: React.FC<StatisticCounterProps> = ({
  statistic,
  isVisible,
  animationDelay,
  duration,
  showProgressBar,
  iconAnimation
}) => {
  const [displayValue, setDisplayValue] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const animationRef = useRef<number>();
  const config = useAnimationConfig();
  const isReducedMotion = useReducedMotion();
  const { trackAnimation } = usePerformanceMonitor();

  // Icon animation spring
  const iconSpring = useSpring({
    scale: isVisible && iconAnimation ? 1 : 0.8,
    rotate: isVisible && iconAnimation ? 0 : -10,
    opacity: isVisible ? 1 : 0.7,
    config: {
      tension: config.customTimings.springConfig.tension,
      friction: config.customTimings.springConfig.friction
    },
    delay: animationDelay * 1000,
    immediate: isReducedMotion
  });

  // Progress bar animation
  const progressSpring = useSpring({
    width: isVisible && showProgressBar 
      ? `${(statistic.value / (statistic.maxValue || statistic.value)) * 100}%` 
      : '0%',
    config: {
      tension: 120,
      friction: 40
    },
    delay: (animationDelay + 0.2) * 1000,
    immediate: isReducedMotion
  });

  // Counter animation logic
  const animateCounter = useCallback(() => {
    if (isReducedMotion) {
      setDisplayValue(statistic.value);
      return;
    }

    if (!isVisible || isAnimating) return;

    setIsAnimating(true);
    trackAnimation.start();

    const startTime = Date.now();
    const startValue = 0;
    const endValue = statistic.value;
    const totalDuration = duration * 1000;
    const delay = animationDelay * 1000;

    const updateValue = () => {
      const elapsed = Date.now() - startTime - delay;
      
      if (elapsed < 0) {
        animationRef.current = requestAnimationFrame(updateValue);
        return;
      }

      const progress = Math.min(elapsed / totalDuration, 1);
      const easedProgress = easingFunctions.easeOutQuart(progress);
      const currentValue = startValue + (endValue - startValue) * easedProgress;
      
      setDisplayValue(currentValue);
      
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(updateValue);
      } else {
        setIsAnimating(false);
        trackAnimation.end();
      }
    };

    animationRef.current = requestAnimationFrame(updateValue);
  }, [statistic.value, duration, animationDelay, isVisible, isAnimating, isReducedMotion, trackAnimation]);

  useEffect(() => {
    animateCounter();
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [animateCounter]);

  // Format the display value
  const formattedValue = displayValue.toFixed(statistic.decimals || 0);
  const IconComponent = statistic.icon;

  return (
    <motion.div
      className="relative bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300"
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ 
        opacity: isVisible ? 1 : 0, 
        y: isVisible ? 0 : 20,
        scale: isVisible ? 1 : 0.95
      }}
      transition={{
        duration: config.customTimings.normal,
        delay: animationDelay,
        ease: config.customTimings.defaultEasing
      }}
    >
      {/* Icon */}
      {IconComponent && (
        <animated.div
          style={{
            ...iconSpring,
            backgroundColor: statistic.color ? `${statistic.color}20` : '#3b82f620'
          }}
          className="inline-flex items-center justify-center w-12 h-12 rounded-lg mb-4"
        >
          <IconComponent 
            className="w-6 h-6" 
            style={{ color: statistic.color || '#3b82f6' }}
          />
        </animated.div>
      )}

      {/* Counter Value */}
      <div className="mb-2">
        <span 
          className="text-3xl font-bold"
          style={{ color: statistic.color || '#1f2937' }}
        >
          {statistic.prefix || ''}{formattedValue}{statistic.suffix || ''}
        </span>
      </div>

      {/* Label */}
      <h3 className="text-lg font-semibold text-gray-900 mb-1">
        {statistic.label}
      </h3>

      {/* Description */}
      {statistic.description && (
        <p className="text-sm text-gray-600 mb-3">
          {statistic.description}
        </p>
      )}

      {/* Progress Bar */}
      {showProgressBar && statistic.maxValue && (
        <div className="mt-4">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Progress</span>
            <span>{Math.round((statistic.value / statistic.maxValue) * 100)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <animated.div
              style={{
                width: progressSpring.width,
                backgroundColor: statistic.color || '#3b82f6'
              }}
              className="h-full rounded-full transition-all duration-300"
            />
          </div>
        </div>
      )}

      {/* Animated background effect */}
      <motion.div
        className="absolute inset-0 rounded-xl opacity-0 pointer-events-none"
        style={{
          background: `linear-gradient(135deg, ${statistic.color || '#3b82f6'}10, transparent)`
        }}
        animate={{
          opacity: isVisible ? [0, 0.5, 0] : 0
        }}
        transition={{
          duration: 2,
          delay: animationDelay + 0.5,
          ease: "easeInOut"
        }}
      />
    </motion.div>
  );
};

// Main AnimatedStatistics component
export const AnimatedStatistics: React.FC<AnimatedStatisticsProps> = ({
  statistics,
  config: userConfig = {},
  className = '',
  triggerOnScroll = true,
  threshold = 0.1
}) => {
  const config = { ...defaultConfig, ...userConfig };
  const animationConfig = useAnimationConfig();
  const isReducedMotion = useReducedMotion();
  const scrollAnimation = useScrollAnimation(threshold, true);
  const { trackAnimation } = usePerformanceMonitor();

  const isVisible = triggerOnScroll ? scrollAnimation.isVisible : true;

  // Calculate animation delays based on style
  const getAnimationDelay = (index: number): number => {
    switch (config.animationStyle) {
      case 'simultaneous':
        return 0;
      case 'wave':
        return Math.sin(index * 0.5) * config.staggerDelay;
      case 'sequential':
      default:
        return index * config.staggerDelay;
    }
  };

  // Layout classes
  const layoutClasses = {
    grid: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6',
    row: 'flex flex-wrap gap-6 justify-center',
    column: 'flex flex-col gap-6 max-w-md mx-auto'
  };

  useEffect(() => {
    if (isVisible) {
      trackAnimation.start();
    } else {
      trackAnimation.end();
    }
  }, [isVisible, trackAnimation]);

  return (
    <AnimatedContainer
      ref={triggerOnScroll ? scrollAnimation.ref : undefined}
      className={`w-full ${className}`}
      triggerOnScroll={false} // We handle scroll trigger manually
    >
      {/* Title and Subtitle */}
      {(config.title || config.subtitle) && (
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ 
            opacity: isVisible ? 1 : 0, 
            y: isVisible ? 0 : -20 
          }}
          transition={{
            duration: animationConfig.customTimings.normal,
            ease: animationConfig.customTimings.defaultEasing
          }}
        >
          {config.title && (
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {config.title}
            </h2>
          )}
          {config.subtitle && (
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {config.subtitle}
            </p>
          )}
        </motion.div>
      )}

      {/* Statistics Grid */}
      <div className={layoutClasses[config.layout]}>
        {statistics.map((statistic, index) => (
          <StatisticCounter
            key={statistic.id}
            statistic={statistic}
            isVisible={isVisible}
            animationDelay={getAnimationDelay(index)}
            duration={config.countingDuration}
            showProgressBar={config.showProgressBars}
            iconAnimation={config.iconAnimation}
          />
        ))}
      </div>
    </AnimatedContainer>
  );
};

// Preset statistics configurations for common use cases
export const statisticsPresets = {
  companyStats: [
    {
      id: 'homes-built',
      label: 'Homes Built',
      value: 250,
      suffix: '+',
      icon: Home,
      color: '#3b82f6',
      description: 'Quality homes delivered',
      maxValue: 300
    },
    {
      id: 'years-experience',
      label: 'Years Experience',
      value: 15,
      suffix: '+',
      icon: Calendar,
      color: '#10b981',
      description: 'Building excellence',
      maxValue: 20
    },
    {
      id: 'happy-families',
      label: 'Happy Families',
      value: 240,
      suffix: '+',
      icon: Users,
      color: '#f59e0b',
      description: 'Living their dream',
      maxValue: 300
    },
    {
      id: 'awards',
      label: 'Awards Won',
      value: 12,
      icon: Award,
      color: '#ef4444',
      description: 'Industry recognition',
      maxValue: 15
    }
  ],
  
  projectStats: [
    {
      id: 'completion-rate',
      label: 'On-Time Completion',
      value: 98.5,
      suffix: '%',
      decimals: 1,
      icon: CheckCircle,
      color: '#10b981',
      description: 'Projects delivered on schedule',
      maxValue: 100
    },
    {
      id: 'satisfaction',
      label: 'Customer Satisfaction',
      value: 4.9,
      suffix: '/5',
      decimals: 1,
      icon: Star,
      color: '#f59e0b',
      description: 'Average customer rating',
      maxValue: 5
    },
    {
      id: 'growth',
      label: 'Annual Growth',
      value: 25,
      suffix: '%',
      icon: TrendingUp,
      color: '#3b82f6',
      description: 'Year over year expansion',
      maxValue: 30
    }
  ]
};

export default AnimatedStatistics;